import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import useThemeStyles from '../theme/useThemeStyles';

const roleTypes = [
  'Maid of Honor',
  'Best Man',
  'Bridesmaid',
  'Groomsman',
  'Flower Girl',
  'Ring Bearer',
  'Mother of the Bride',
  'Father of the Bride',
  'Mother of the Groom',
  'Father of the Groom',
  'Officiant',
  'Other'
];

function WeddingPartyRoles() {
  const styles = useThemeStyles();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    responsibilities: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const getRoleLabel = (role, customTitle) => {
    if (customTitle) return customTitle;
    
    const labels = {
      maidOfHonor: 'Maid of Honor',
      bestMan: 'Best Man',
      bridesmaid: 'Bridesmaid',
      groomsman: 'Groomsman',
      flowerGirl: 'Flower Girl',
      ringBearer: 'Ring Bearer',
      father: 'Father',
      mother: 'Mother',
      officiant: 'Officiant',
      usher: 'Usher',
      other: 'Other'
    };
    return labels[role] || role;
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/wedding-party-roles');
      setRoles(response.data);
    } catch (error) {
      setError('Failed to load wedding party roles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      role: '',
      email: '',
      phone: '',
      responsibilities: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    // Map backend role format back to display format
    const roleDisplayMap = {
      'maidOfHonor': 'Maid of Honor',
      'bestMan': 'Best Man',
      'bridesmaid': 'Bridesmaid',
      'groomsman': 'Groomsman',
      'flowerGirl': 'Flower Girl',
      'ringBearer': 'Ring Bearer',
      'mother': role.customRoleTitle || 'Mother',
      'father': role.customRoleTitle || 'Father',
      'officiant': 'Officiant',
      'usher': 'Usher',
      'other': role.customRoleTitle || 'Other'
    };
    
    const displayRole = roleDisplayMap[role.role] || role.role;
    
    setFormData({
      name: role.guest?.name || role.name || '',
      role: displayRole,
      email: role.guest?.email || role.email || '',
      phone: role.guest?.phone || role.phone || '',
      responsibilities: role.notes || role.responsibilities || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/wedding-party-roles/${id}`);
      setRoles(roles.filter(r => r._id !== id));
      setSuccess('Role deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete role');
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      if (editingRole) {
        // For editing, update both the guest information and the role assignment
        const guestId = editingRole.guest?._id || editingRole.guest;
        
        // Update the guest information
        if (guestId) {
          const guestUpdateData = {
            name: formData.name,
            email: formData.email || '',
            phone: formData.phone || ''
          };
          await api.put(`/api/guests/${guestId}`, guestUpdateData);
        }
        
        // Map role to backend format
        const roleMapping = {
          'Maid of Honor': { role: 'maidOfHonor', customTitle: '' },
          'Best Man': { role: 'bestMan', customTitle: '' },
          'Bridesmaid': { role: 'bridesmaid', customTitle: '' },
          'Groomsman': { role: 'groomsman', customTitle: '' },
          'Flower Girl': { role: 'flowerGirl', customTitle: '' },
          'Ring Bearer': { role: 'ringBearer', customTitle: '' },
          'Mother of the Bride': { role: 'mother', customTitle: 'Mother of the Bride' },
          'Father of the Bride': { role: 'father', customTitle: 'Father of the Bride' },
          'Mother of the Groom': { role: 'mother', customTitle: 'Mother of the Groom' },
          'Father of the Groom': { role: 'father', customTitle: 'Father of the Groom' },
          'Officiant': { role: 'officiant', customTitle: '' },
          'Other': { role: 'other', customTitle: '' }
        };
        
        const roleInfo = roleMapping[formData.role] || { 
          role: formData.role.toLowerCase().replace(/\s+/g, ''), 
          customTitle: formData.role 
        };
        
        // Update the role assignment (including role, notes, and customRoleTitle)
        const updateData = {
          role: roleInfo.role, // Send the role so backend can update it if changed
          notes: formData.responsibilities || '',
          customRoleTitle: roleInfo.customTitle
        };
        await api.put(`/api/wedding-party-roles/${editingRole._id}`, updateData);
        
        // Refresh the roles list to get updated guest information
        await fetchRoles();
        setSuccess('Role updated successfully');
      } else {
        // For new role, we need to create or find a guest first
        let guestId = null;
        
        // Check if we have an email to search for existing guest
        if (formData.email) {
          try {
            const guestsResponse = await api.get('/api/guests');
            const existingGuest = guestsResponse.data.find(
              g => g.email && g.email.toLowerCase() === formData.email.toLowerCase()
            );
            if (existingGuest) {
              guestId = existingGuest._id;
            }
          } catch (err) {}
        }
        
        // If no existing guest found, create a new one
        if (!guestId) {
          const guestData = {
            name: formData.name,
            email: formData.email || `${formData.name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
            phone: formData.phone || ''
          };
          const newGuest = await api.post('/api/guests', guestData);
          guestId = newGuest.data._id;
        }
        
        // Map role to backend format (convert display names to backend enum values)
        const roleMapping = {
          'Maid of Honor': { role: 'maidOfHonor', customTitle: '' },
          'Best Man': { role: 'bestMan', customTitle: '' },
          'Bridesmaid': { role: 'bridesmaid', customTitle: '' },
          'Groomsman': { role: 'groomsman', customTitle: '' },
          'Flower Girl': { role: 'flowerGirl', customTitle: '' },
          'Ring Bearer': { role: 'ringBearer', customTitle: '' },
          'Mother of the Bride': { role: 'mother', customTitle: 'Mother of the Bride' },
          'Father of the Bride': { role: 'father', customTitle: 'Father of the Bride' },
          'Mother of the Groom': { role: 'mother', customTitle: 'Mother of the Groom' },
          'Father of the Groom': { role: 'father', customTitle: 'Father of the Groom' },
          'Officiant': { role: 'officiant', customTitle: '' },
          'Other': { role: 'other', customTitle: '' }
        };
        
        const roleInfo = roleMapping[formData.role] || { 
          role: formData.role.toLowerCase().replace(/\s+/g, ''), 
          customTitle: formData.role 
        };
        
        // Now assign the role
        const roleData = {
          guestId: guestId,
          role: roleInfo.role,
          notes: formData.responsibilities || '',
          customRoleTitle: roleInfo.customTitle
        };
        
        const response = await api.post('/api/wedding-party-roles', roleData);
        setRoles([...roles, response.data]);
        setSuccess('Role added successfully');
      }
      setDialogOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save role';
      setError(errorMessage);
    }
  };

  const getRoleColor = (role) => {
    if (role.includes('Honor') || role.includes('Best')) return 'primary';
    if (role.includes('Bride')) return 'secondary';
    if (role.includes('Groom')) return 'info';
    if (role.includes('Parent') || role.includes('Mother') || role.includes('Father')) return 'warning';
    return 'default';
  };

  const groupRolesByType = () => {
    const grouped = {
      'Bride\'s Side': roles.filter(r => {
        const roleValue = r.role;
        const customTitle = r.customRoleTitle || '';
        return roleValue === 'maidOfHonor' || roleValue === 'bridesmaid' || 
               customTitle === 'Mother of the Bride' || customTitle === 'Father of the Bride';
      }),
      'Groom\'s Side': roles.filter(r => {
        const roleValue = r.role;
        const customTitle = r.customRoleTitle || '';
        return roleValue === 'bestMan' || roleValue === 'groomsman' || 
               customTitle === 'Mother of the Groom' || customTitle === 'Father of the Groom';
      }),
      'Special Roles': roles.filter(r => {
        const roleValue = r.role;
        return roleValue === 'flowerGirl' || roleValue === 'ringBearer' || 
               roleValue === 'officiant' || roleValue === 'other' || roleValue === 'usher';
      })
    };
    return grouped;
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  const groupedRoles = groupRolesByType();

  return (
    <Box sx={styles.container.page}>
      <Box sx={{
        ...styles.patterns.pageHeader,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: { xs: 'center', sm: 'left' } }}>
          Wedding Party Roles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="medium"
          sx={{ alignSelf: { xs: 'stretch', sm: 'inherit' }, minWidth: { xs: 'auto', sm: undefined } }}
        >
          Add Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {roles.length === 0 ? (
        <Card sx={styles.card.default}>
          <CardContent>
            <Box sx={styles.patterns.emptyState}>
              <GroupIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No wedding party members yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Start assigning roles to your wedding party members
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
              >
                Add First Member
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(groupedRoles).map(([group, groupRoles]) => {
            if (groupRoles.length === 0) return null;
            
            return (
              <Grid item xs={12} md={4} key={group}>
                <Card sx={styles.card.default}>
                  <CardContent>
                    <Typography variant="h6" sx={{ ...styles.text.sectionTitle, mb: 2 }}>
                      {group}
                    </Typography>
                    
                    <List>
                      {groupRoles.map((role) => (
                        <ListItem
                          key={role._id}
                          sx={{
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                          <ListItemText
                            primary={role.guest?.name || role.name}
                            secondary={
                              <Box>
                                <Chip 
                                  label={getRoleLabel(role.role, role.customRoleTitle)} 
                                  size="small" 
                                  color={getRoleColor(role.customRoleTitle || role.role)}
                                  sx={{ mt: 0.5 }}
                                />
                                {(role.guest?.email || role.email) && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {role.guest?.email || role.email}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => handleEdit(role)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(role._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Wedding Party Member' : 'Add Wedding Party Member'}
        </DialogTitle>
        <DialogContent>
          <Box sx={styles.patterns.formContainer} mt={2}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                {roleTypes.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            
            <TextField
              fullWidth
              label="Responsibilities"
              multiline
              rows={3}
              value={formData.responsibilities}
              onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
              placeholder="What are their specific duties?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name || !formData.role}
          >
            {editingRole ? 'Update' : 'Add'} Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WeddingPartyRoles; 